import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./motion";
import { IMAGES } from "./data";
import { scrollTo } from "./Header";

const SERVICES = [
  {
    n: "01",
    title: "Individual Therapy",
    tagline: "One-to-one psychological therapy",
    body:
      "Confidential, evidence-based therapy for adults navigating anxiety, low mood, burnout, life transitions and trauma — a calm, unhurried space to move forward.",
    image: IMAGES.therapyRoom,
    span: "lg:col-span-7",
    tall: true,
    accent: "#2FD6B8",
  },
  {
    n: "02",
    title: "Parents of Neurodivergent Children",
    tagline: "Guidance, strategies & reassurance",
    body:
      "Compassionate, practical support to understand your child's world and parent with confidence — grounded in behavioural science.",
    image: IMAGES.parentChild,
    span: "lg:col-span-5",
    tall: true,
    accent: "#E0619B",
  },
  {
    n: "03",
    title: "Psychological Assessments",
    tagline: "Clarity through thorough assessment",
    body:
      "Comprehensive psychological and cognitive assessment carried out with care and rigour, giving you a clear formulation and recommendations you can act on.",
    image: null,
    span: "lg:col-span-5",
    tall: false,
    accent: "#38A4E0",
  },
  {
    n: "04",
    title: "Events & Workshops",
    tagline: "Learn together, grow together",
    body:
      "Talks, parent clinics and small-group workshops on neurodiversity, wellbeing and evidence-based parenting.",
    image: null,
    span: "lg:col-span-7",
    tall: false,
    accent: "#F0862F",
    action: { label: "See upcoming events", id: "events" },
  },
];

const Card = ({ s }) => (
  <div
    data-testid={`service-card-${s.n}`}
    className={`group relative overflow-hidden rounded-[2rem] border border-border bg-card ${s.span} ${
      s.tall ? "min-h-[440px]" : "min-h-[300px]"
    } flex flex-col justify-between p-8 lg:p-10 transition-all duration-500 hover:-translate-y-1`}
    style={{ "--accent": s.accent }}
  >
    {/* Top accent bar */}
    <span
      className="absolute inset-x-0 top-0 h-1.5 origin-left scale-x-100 transition-transform duration-500"
      style={{ background: s.accent }}
    />

    {/* Soft coloured corner glow (visible on text-only cards) */}
    {!s.image && (
      <span
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl opacity-25 group-hover:opacity-45 transition-opacity duration-700"
        style={{ background: s.accent }}
      />
    )}

    {s.image && (
      <>
        <img
          src={s.image}
          alt={s.title}
          className="absolute inset-0 h-full w-full object-cover opacity-0 scale-105 transition-all duration-700 group-hover:opacity-100 group-hover:scale-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-700 mix-blend-overlay"
          style={{ background: `linear-gradient(140deg, ${s.accent}, transparent 70%)` }}
        />
      </>
    )}

    <div className="relative z-10 flex items-start justify-between">
      <span
        className="font-serif text-5xl font-light group-hover:text-primary-foreground transition-colors duration-500"
        style={{ color: s.accent }}
      >
        {s.n}
      </span>
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.accent }} />
    </div>

    <div className="relative z-10">
      <p
        className="text-xs font-semibold uppercase tracking-[0.22em] mb-3 transition-colors duration-500"
        style={{ color: s.accent }}
      >
        {s.tagline}
      </p>
      <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-primary group-hover:text-primary-foreground transition-colors duration-500 max-w-md">
        {s.title}
      </h3>
      <p className="mt-4 text-base font-light leading-relaxed max-w-md text-text-secondary group-hover:text-prism-cream/90 transition-colors duration-500" style={{ color: "hsl(var(--muted-foreground))" }}>
        {s.body}
      </p>
      {s.action && (
        <button
          data-testid={`service-action-${s.n}`}
          onClick={() => scrollTo(s.action.id)}
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary hover:gap-3 transition-all duration-300"
        >
          {s.action.label} <ArrowUpRight size={16} />
        </button>
      )}
    </div>
  </div>
);

export const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-36"
  >
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
      <Reveal>
        <p
          className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.28em] mb-5"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          <span className="h-1.5 w-6 rounded-full prism-line" /> How I can help
        </p>
        <h2 className="font-serif font-light tracking-tight leading-tight text-primary text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
          Care shaped around you
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p
          className="max-w-sm text-base font-light leading-relaxed"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          Every person and family arrives with a different story. These are the
          ways we can work together.
        </p>
      </Reveal>
    </div>

    <Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {SERVICES.map((s) => (
          <Card key={s.n} s={s} />
        ))}
      </div>
    </Reveal>
  </section>
);
